import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Select } from '@nyce/admin-kit/ui';
import {
  ActionsRow,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { errorMessage } from '../../lib/errors';
import { formatDateTime } from '../../lib/format';
import { useParam } from '../../pages/statistics/shared';
import { billing, download, useBillingWrite, type BillingRun, type FileFormat } from '../api/client';
import { RunStatusChip, SendDialog, reportTitle, targetLabel } from '../shared';
import '../../pages/statistics/statistics.css';

const PAGE = 50;

/** Every report run, newest first: who ran it (an operator or a schedule), where it went; download or send again. */
export function BillingRunsPage() {
  const catalog = useAsync(() => billing.catalog(), []);
  const [reportId, setReportId] = useParam('report', '');
  const [scheduleId] = useParam('schedule', '');
  const [pages, setPages] = useState<BillingRun[][]>([]);
  const list = useAsync(async () => {
    const first = await billing.runs({ reportId, scheduleId, limit: PAGE });
    setPages([first]);
    return first;
  }, [reportId, scheduleId]);
  const [sending, setSending] = useState<BillingRun | null>(null);
  const [more, setMore] = useState(false);
  const canWrite = useBillingWrite();
  const toast = useNotice();
  const reports = catalog.data?.reports ?? [];
  const runs = pages.flat();
  const last = pages[pages.length - 1];

  const loadMore = async () => {
    setMore(true);
    try {
      const next = await billing.runs({
        reportId,
        scheduleId,
        limit: PAGE,
        before: runs[runs.length - 1]?.id,
      });
      setPages((p) => [...p, next]);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setMore(false);
    }
  };
  const save = async (r: BillingRun, format: FileFormat) => {
    try {
      await download(r.id, format);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const remove = async (r: BillingRun) => {
    try {
      await billing.deleteRun(r.id);
      setPages((p) => p.map((page) => page.filter((x) => x.id !== r.id)));
      toast.success(`Deleted run #${r.id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <Page
      title="Billing history"
      description="Every saved report run and where it was sent. Files are rendered from the stored run, so they match what was sent."
    >
      {toast.element}
      <div className="sx-toolbar">
        <Select
          label="Report"
          value={reportId}
          onChange={setReportId}
          options={[
            { value: '', label: 'All reports' },
            ...reports.map((r) => ({ value: r.id, label: r.title })),
          ]}
        />
        {scheduleId && (
          <span className="nyk-ui-hint">
            Schedule <b>{scheduleId}</b> · <Link to="/billing/runs">all runs</Link>
          </span>
        )}
      </div>
      <ErrorBox error={list.error} />
      <Loading loading={list.loading && !list.data} />
      <div className="nyk-card">
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="billing-runs">
            <thead>
              <tr>
                <th>Run</th>
                <th>Report</th>
                <th>Period</th>
                <th>Status</th>
                <th>By</th>
                <th>Sent to</th>
              </tr>
            </thead>
            {runs.map((r) => (
              <tbody key={r.id} className="nyk-row-group" data-run={r.id}>
                <tr>
                  <td className="nyk-card-title">
                    <Link to={`/billing/reports?run=${r.id}`}>#{r.id}</Link>
                    <div className="nyk-ui-hint">{formatDateTime(r.createdAt)}</div>
                  </td>
                  <td data-label="Report">
                    {reportTitle(reports, r.reportId)}
                    <div className="nyk-ui-hint">{targetLabel(reports, r.reportId, r.target)}</div>
                  </td>
                  <td data-label="Period">{r.periodLabel}</td>
                  <td data-label="Status">
                    <RunStatusChip run={r} />
                    {(r.error || r.sendError) && (
                      <div className="nyk-text-red nyk-wrap">{r.error ?? r.sendError}</div>
                    )}
                  </td>
                  <td data-label="By">{r.scheduleId ? `schedule ${r.scheduleId}` : r.createdBy}</td>
                  <td data-label="Sent to" className="nyk-wrap">
                    {r.sentAt ? (
                      <>
                        {r.recipients.join(', ')}
                        <div className="nyk-ui-hint">{formatDateTime(r.sentAt)}</div>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
                {(r.status === 'succeeded' || (canWrite && r.status === 'failed')) && (
                  <ActionsRow colSpan={6}>
                    {r.status === 'succeeded' && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => void save(r, 'pdf')}>
                          PDF
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => void save(r, 'xlsx')}>
                          Excel
                        </Button>
                        {canWrite && (
                          <Button size="sm" variant="ghost" onClick={() => setSending(r)}>
                            {r.sentAt ? 'Send again…' : 'Send…'}
                          </Button>
                        )}
                      </>
                    )}
                    {canWrite && (
                      <ConfirmButton
                        size="sm"
                        variant="danger"
                        message={`Delete run #${r.id} (${r.periodLabel})${r.sentAt ? ', which was sent,' : ''} from the history?`}
                        onConfirm={() => void remove(r)}
                      >
                        Delete
                      </ConfirmButton>
                    )}
                  </ActionsRow>
                )}
              </tbody>
            ))}
          </table>
        </TableScroller>
        {list.data && !runs.length && <EmptyState title="No report runs yet." />}
        {last?.length === PAGE && (
          <div className="nyk-pager">
            <Button size="sm" variant="ghost" loading={more} onClick={() => void loadMore()}>
              Older runs
            </Button>
          </div>
        )}
      </div>
      {sending && (
        <SendDialog
          run={{ ...sending, title: reportTitle(reports, sending.reportId) }}
          initial={sending.recipients}
          onSent={(r) => {
            setPages((p) => p.map((page) => page.map((x) => (x.id === r.id ? r : x))));
            toast.success(`Sent to ${r.recipients.join(', ')}`);
          }}
          onClose={() => setSending(null)}
        />
      )}
    </Page>
  );
}
